// Shared inline-cell field primitives for the Flow2 Goals table. These mirror the
// field rendering in components/organisms/GoalDrawer/index.tsx (renderField) and the
// per-drawer priority/estimate helpers, so the inline table matches the drawer 1:1.
import { CSSProperties, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Box, useTheme } from '@mui/material';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { DEFAULT_CURRENCY_SYMBOL, FEATURE_NAMES } from '../../../common/constants';
import { useAppSelector } from '../../../common/hooks';
import { BasicDatePicker, ComboSelect, TextboxInput, Typography } from '../../../components/atoms';
import { InfoTooltip } from '../../../components/molecules';
import { GoalPriorityInfoText } from '../../../components/molecules/GoalPriorityInfoText/GoalPriorityInfoText';
import {
    checkFFEnabled,
    exceedsDigitLimit,
    formatCurrency,
    getGoalPriorityEnum,
    getPriorityLabel,
    isAspirePriorityTenant,
    priorityOptionsFlow2,
    removeNonNumeric
} from '../../../utils';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info-goals-page.svg';
import type { RowEditContext } from '../../../components/molecules/EditableTable';
import type { GoalInlineRow } from '../goalInlineHelpers';

export type GoalRowCtx = RowEditContext<GoalInlineRow>;

/**
 * Figma "Typescale/Normal" text styles for the Goals read cells. Scoped sx overrides that keep
 * the shared Typography variants untouched; colours use tenant-aware semantic tokens.
 */
export const goalsReadTextSx = (theme: any) => ({
    // 16px body, dark — goal name / amount value / date
    value: { fontWeight: 400, fontSize: '16px', lineHeight: '22px', color: theme.palette.semantic.text.default },
    // 16px body, muted — goal-type label
    typeLabel: { fontWeight: 400, fontSize: '16px', lineHeight: '22px', color: theme.palette.semantic.text.muted },
    // 14px, muted — amount label / frequency
    caption: {
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '19px',
        letterSpacing: 0,
        color: theme.palette.semantic.text.muted
    }
});

/** Figma: dropdown placeholders are muted grey + upright (overrides ComboSelect's default blue italic). */
export const mutedPlaceholderStyle = (theme: any): CSSProperties => ({
    color: theme.palette.semantic.text.muted,
    fontStyle: 'normal'
});

/** Currency text input (mirrors the drawer's 'currency' field). */
export const CurrencyField = ({
    ctx,
    name,
    required = false,
    dataTestId,
    requiredMessage,
    maxDigitsMessage,
    placeholder
}: {
    ctx: GoalRowCtx;
    name: keyof GoalInlineRow & string;
    required?: boolean;
    dataTestId?: string;
    requiredMessage?: string;
    maxDigitsMessage?: string;
    placeholder?: string;
}) => {
    const { t } = useTranslation();
    const currencySymbol = useAppSelector((state) => state.global?.globalConfig?.currencySymbol);
    // eslint-disable-next-line no-unused-vars
    const validators: Record<string, (value: unknown) => true | string> = {};
    if (required) {
        validators.required = (value) => !!value || (requiredMessage ?? t('TEXT_THIS_IS_REQUIRED_FIELD'));
    }
    if (maxDigitsMessage) {
        validators.maxDigits = (value) => !exceedsDigitLimit(value, 15) || maxDigitsMessage;
    }
    const rules = Object.keys(validators).length ? { validate: validators } : undefined;
    return (
        <Controller
            name={name}
            control={ctx.control}
            rules={rules}
            render={({ field }) => (
                <TextboxInput
                    onBlur={field.onBlur}
                    onChange={(e) => {
                        const numericValue = Number(removeNonNumeric(e.target.value));
                        ctx.setValue(name, (isNaN(numericValue) ? 0 : numericValue) as never, {
                            shouldValidate: true,
                            shouldDirty: true
                        });
                    }}
                    value={field.value ? formatCurrency(Number(field.value), true) : ''}
                    inputRef={field.ref}
                    size="small"
                    error={!!ctx.errors[name]}
                    showTooltipError={true}
                    openTooltipError={!!ctx.errors[name] && Object.keys(ctx.errors).length === 1}
                    helperText={ctx.errors[name]?.message as string | undefined}
                    type="text"
                    data-testid={dataTestId || name}
                    placeholder={placeholder ?? (currencySymbol || DEFAULT_CURRENCY_SYMBOL)}
                    iconPlacement="start"
                    fullWidth
                />
            )}
        />
    );
};

/** Decorative "Estimate the goal amount" helper (matches the drawer; no handler wired). */
export const EstimateGoalAmountHelper = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    return (
        <Box mt="4px" display="flex" alignItems="center" gap="4px">
            <InfoIcon />
            <Typography
                variant="body.smallDemibold"
                sx={{ color: theme.palette.info[300], textDecoration: 'underline' }}
                label={t('TEXT_GOAL_TARGET_AMOUNT_HELPER_TEXT')}
            />
        </Box>
    );
};

/** Frequency select (Annually / Monthly). Optionally clears date fields on change (income). */
export const FrequencySelect = ({
    ctx,
    name = 'frequency',
    required = false,
    clearDateFields
}: {
    ctx: GoalRowCtx;
    name?: keyof GoalInlineRow & string;
    required?: boolean;
    clearDateFields?: (keyof GoalInlineRow & string)[];
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const options = [
        { label: t('ANNUAL'), value: 'Annually' },
        { label: t('MONTHLY'), value: 'Monthly' }
    ];
    return (
        <Controller
            name={name}
            control={ctx.control}
            rules={required ? { required: t('TEXT_THIS_IS_REQUIRED_FIELD') } : undefined}
            render={({ field }) => (
                <ComboSelect
                    {...field}
                    onSelectChange={(value: string) => {
                        ctx.setValue(name, value as never, { shouldValidate: true, shouldDirty: true });
                        if (clearDateFields?.length) {
                            clearDateFields.forEach((f) =>
                                ctx.setValue(f, null as never, {
                                    shouldValidate: false,
                                    shouldDirty: false,
                                    shouldTouch: false
                                })
                            );
                            ctx.clearErrors(clearDateFields as never);
                        }
                    }}
                    size="small"
                    selectedValue={(field.value as string) ?? ''}
                    options={options}
                    placeholder={t('FREQUENCY')}
                    placeholderStyle={mutedPlaceholderStyle(theme)}
                    data-testid={name}
                    error={!!ctx.errors[name]}
                />
            )}
        />
    );
};

/** Single date field (mirrors the drawer's 'date' field via BasicDatePicker). */
export const InlineDateField = ({
    ctx,
    name,
    required = false,
    isMMYYYY = true,
    isYYYY = false,
    enabled = true,
    startDate,
    endDate,
    showDateError = false,
    dateErrorMsg,
    compareDate,
    skipValidation = false,
    pastDateErrorMsg
}: {
    ctx: GoalRowCtx;
    name: keyof GoalInlineRow & string;
    required?: boolean;
    isMMYYYY?: boolean;
    isYYYY?: boolean;
    enabled?: boolean;
    startDate?: string;
    /** Upper bound passed to the picker (maxDate); locks a saved goal's date to its tenure window. */
    endDate?: string;
    /** When true, also validates `value` against `compareDate` (e.g. income end vs start date). */
    showDateError?: boolean;
    /** Overrides the default 'select a future date' text, e.g. for "end must be after start". */
    dateErrorMsg?: string;
    /** Overrides the default message when a past date is entered. */
    pastDateErrorMsg?: string;
    /** Date the value must fall after when `showDateError` is set; defaults to `startDate`. */
    compareDate?: string;
    /** Locked fields the user can't edit: suppress all validation and error display. */
    skipValidation?: boolean;
}) => {
    const { t } = useTranslation();
    // Reason string from the picker's own onError (e.g. 'minDate' | 'maxDate' | 'invalidDate'), or null.
    const [pickerErrorType, setPickerErrorType] = useState<string | null>(null);

    return (
        <Controller
            name={name}
            control={ctx.control}
            rules={{
                validate: (v) => {
                    if (skipValidation) return true;
                    if (required && !v) return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    if (!v) return true;

                    const dateValue = dayjs(v as unknown as Dayjs | string);
                    // Invalid dates (e.g. a 0000 year) fail every comparison, so reject them explicitly.
                    if (!dateValue.isValid()) return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    if (dateValue.isBefore(dayjs(), 'day')) {
                        return pastDateErrorMsg || t('GOALS_INLINE_DATE_ERROR');
                    }

                    if (showDateError) {
                        const compareValue = compareDate ?? startDate;
                        if (compareValue) {
                            const comparisonDate = dayjs(compareValue);
                            if (!dateValue.isAfter(comparisonDate, 'day')) {
                                return dateErrorMsg || t('END_DATE_AFTER_START_ERROR');
                            }
                        }
                    }

                    return true;
                }
            }}
            render={({ field }) => {
                const fieldErrorMsg = ctx.errors[name]?.message as string | undefined;
                // The picker can flag a bound violation (start > end) before RHF sets a message,
                // so give its border a matching tooltip message instead of an empty one.
                const pickerErrorMsg = pickerErrorType ? dateErrorMsg || t('GOALS_INLINE_DATE_ERROR') : undefined;
                const errorMsg = skipValidation ? undefined : fieldErrorMsg || pickerErrorMsg;
                return (
                    <BasicDatePicker
                        enabled={enabled}
                        type="single"
                        startDate={startDate || dayjs().startOf('month').format()}
                        endDate={endDate}
                        value={(field.value ?? null) as unknown as Dayjs | null}
                        onChange={(value: string | Dayjs | null) => {
                            let next = value;
                            if ((isMMYYYY || isYYYY) && value) next = dayjs(value).startOf('month');
                            field.onChange(next);
                        }}
                        onBlur={field.onBlur}
                        data-testid={name}
                        isMmYyyy={isMMYYYY}
                        isYyyy={isYYYY}
                        hasError={!!errorMsg}
                        minErrorMsg={errorMsg}
                        onValidationError={(dateError: any, value: Dayjs | null) => {
                            if (skipValidation) {
                                setPickerErrorType(null);
                                ctx.clearErrors(name);
                                return;
                            }
                            setPickerErrorType(dateError ?? null);
                            // An invalid entry (e.g. a 0000 year) never reaches field.onChange, so RHF's
                            // validate never runs; register/clear the picker error directly to gate Save.
                            if (dateError) {
                                const dateValue = value ? dayjs(value) : null;
                                const pastDateMessage =
                                    dateError === 'minDate' &&
                                    dateValue?.isValid() &&
                                    dateValue.isBefore(dayjs(), 'day')
                                        ? pastDateErrorMsg
                                        : undefined;
                                ctx.setError(name, {
                                    type: 'manual',
                                    message: pastDateMessage || dateErrorMsg || t('TEXT_THIS_IS_REQUIRED_FIELD')
                                });
                            } else {
                                ctx.clearErrors(name);
                            }
                        }}
                    />
                );
            }}
        />
    );
};

/** Priority ComboSelect (mirrors the drawer priority customRender: "N - Label", Wish->Aspire). */
export const PriorityEditSelect = ({ ctx }: { ctx: GoalRowCtx }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { goeConfig } = useAppSelector((state) => state.global);
    const goalPriorityValue = goeConfig?.['goalPriorityProbabilities'];
    const priorityOptions = priorityOptionsFlow2(t, getGoalPriorityEnum(goalPriorityValue));
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const displayLabel = (label: string, value: string) => getPriorityLabel({ label, value }, tenant, t);
    return (
        <Controller
            name="goalPriority"
            control={ctx.control}
            rules={{ required: t('TEXT_THIS_IS_REQUIRED_FIELD') }}
            render={({ field }) => (
                <ComboSelect
                    onSelectChange={(val: string) =>
                        ctx.setValue('goalPriority', val as never, { shouldValidate: true, shouldDirty: true })
                    }
                    onBlur={field.onBlur}
                    selectedValue={(field.value as string) || ''}
                    name="inline-goal-priority"
                    placeholder={t('PRIORITY')}
                    options={priorityOptions}
                    error={!!ctx.errors.goalPriority}
                    showTooltipError
                    openTooltipError={!!ctx.errors.goalPriority && Object.keys(ctx.errors).length === 1}
                    errorText={ctx.errors.goalPriority?.message as string | undefined}
                    listItemStyle={{ padding: '0px 14px' }}
                    renderMenuItem={(option: { label: string; value: string }, index = 0) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            {`${index + 1} - ${displayLabel(option.label, option.value)}`}
                        </Box>
                    )}
                    renderValue={(selected: string) => {
                        const selectedOption = priorityOptions.find((option) => option.value === selected);
                        if (selectedOption) {
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {`${selectedOption.index + 1} - ${displayLabel(
                                        selectedOption.label,
                                        selectedOption.value
                                    )}`}
                                </Box>
                            );
                        }
                        return <em style={mutedPlaceholderStyle(theme)}>{t('PRIORITY')}</em>;
                    }}
                />
            )}
        />
    );
};

/** Info tooltip used next to a column header / field label. */
export const HeaderInfoTooltip = ({ i18nKey }: { i18nKey: string }) => {
    const { t } = useTranslation();
    return <InfoTooltip message={<span>{t(i18nKey)}</span>} />;
};

/** GoalPriority info tooltip content (percentages from GOE config), reused from the drawer. */
export const PriorityInfoTooltipContent = () => {
    const { goeConfig } = useAppSelector((state) => state.global);
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const goalPriorityValue = goeConfig?.['goalPriorityProbabilities'];
    const valuesInPercentage =
        goalPriorityValue &&
        Object.fromEntries(
            Object.entries(goalPriorityValue || {}).map(([key, value]) => [key, (value as number) * 100])
        );
    // FTDS revamp (AEX-3428): the header tooltip lists each priority as a full sentence with its
    // config-driven target probability; the legacy drawer content is untouched.
    if (checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])) {
        return <PriorityInfoTooltipContentRevamp values={valuesInPercentage} tenant={tenant} />;
    }
    return <GoalPriorityInfoText values={valuesInPercentage} renameWishToAspire={isAspirePriorityTenant(tenant)} />;
};

// Priority rows in display order; `wish` is surfaced as "Aspire" only for the aspire tenant (banortemex).
const PRIORITY_TOOLTIP_ROWS: { key: string; labelKey: string }[] = [
    { key: 'need', labelKey: 'NEED' },
    { key: 'want', labelKey: 'WANT' },
    { key: 'wish', labelKey: 'ASPIRE' },
    { key: 'dream', labelKey: 'DREAM' },
    { key: 'desire', labelKey: 'DESIRE' }
];

const PriorityInfoTooltipContentRevamp = ({ values, tenant }: { values?: Record<string, unknown>; tenant?: string }) => {
    const { t } = useTranslation();
    const readValue = (key: string) => {
        const capitalized = `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
        const raw = values?.[key] ?? values?.[capitalized];
        return typeof raw === 'number' && !Number.isNaN(raw) ? raw : undefined;
    };
    const rows = PRIORITY_TOOLTIP_ROWS.map((row) => ({
        ...row,
        labelKey: row.key === 'wish' && !isAspirePriorityTenant(tenant) ? 'WISH' : row.labelKey,
        value: readValue(row.key)
    })).filter(
        (row) => row.value !== undefined
    );
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {rows.map((row) => (
                <span key={row.key}>
                    <Trans
                        i18nKey="GOALS_INLINE_PRIORITY_TOOLTIP_ROW"
                        values={{ label: t(row.labelKey), value: Math.round(row.value as number) }}
                        components={{ strong: <strong /> }}
                    />
                </span>
            ))}
            <span>{t('GOALS_INLINE_PRIORITY_TOOLTIP_OUTRO')}</span>
        </Box>
    );
};
